import type { Application, ApplicationClientFinancialObligation } from '../types';
import { obligationStageLabel } from './obligationStageUi';

/** Поля для ObligationPaymentsBlock (карточка заявки / drawer). */
export type ApplicationIncomeObligationBlockItem = {
  id?: number;
  direction?: string;
  dueDate?: string | null;
  remainingAmount?: number;
  amountWithVAT?: number;
  totalPaid?: number;
  stage?: string;
  triggerCode?: string;
  triggerDate?: string | null;
  delayDays?: number | null;
  isActive?: boolean;
  status?: string;
};

export type ApplicationClientSettlement = {
  transportCost: number | null;
  received: number;
  remaining: number;
  paymentStatus: string;
};

export type ApplicationClientObligationRow = {
  id: number;
  stage: string;
  stageLabel: string;
  amount: number;
  remaining: number;
  dueDate: string | null;
  paidAt: string | null;
  status: string;
};

export type ApplicationClientPaymentRow = {
  key: string;
  paymentDate: string | null;
  /** DD.MM.YYYY или «Дата не указана» для отображения */
  dateLabel: string;
  amount: number;
};

function obligationPaid(o: ApplicationClientFinancialObligation): number {
  const fromApplied = Number(o.appliedAmount ?? NaN);
  const fromPayments = (o.payments ?? []).reduce((s, p) => s + Number(p.amountPaid ?? 0), 0);
  const fromAlloc = (o.paymentAllocations ?? []).reduce((s, a) => s + Number(a.amount ?? 0), 0);
  const summed = Math.round((fromPayments + fromAlloc + (Number.isFinite(fromApplied) ? fromApplied : 0)) * 100) / 100;
  if (summed > 0) return summed;
  if (Number.isFinite(Number(o.paid_amount))) return Number(o.paid_amount);
  const planned = Number(o.amountWithVAT ?? 0);
  const rem = Number((o as { remaining_amount?: number }).remaining_amount ?? NaN);
  if (Number.isFinite(rem)) return Math.max(0, planned - rem);
  return 0;
}

/** Сумма оплат по клиентскому обязательству (как в ApplicationDetailsPanel). */
export function sumPaidClientObligation(obl: ApplicationClientFinancialObligation): number {
  return obligationPaid(obl);
}

export function mapApplicationIncomeObligationToBlockItem(
  obl: ApplicationClientFinancialObligation
): ApplicationIncomeObligationBlockItem {
  const ob = obl as ApplicationClientFinancialObligation & {
    triggerCode?: string;
    delayDays?: number | null;
  };
  const paid = obligationPaid(obl);
  const amt = Number(obl.planned_amount ?? obl.amountWithVAT ?? 0);
  const remaining = Number.isFinite(Number(obl.remaining_amount))
    ? Math.max(0, Number(obl.remaining_amount))
    : Math.max(0, amt - paid);
  const delayFromApi = obl.delay_days;
  const delayFallback = ob.delayDays;
  return {
    id: obl.id,
    direction: obl.direction,
    dueDate: obl.dueDate ?? null,
    remainingAmount: remaining,
    amountWithVAT: amt,
    totalPaid: paid,
    stage: obl.stage,
    triggerCode: ob.triggerCode,
    triggerDate: obl.baseDate ?? null,
    delayDays:
      delayFromApi != null && Number.isFinite(Number(delayFromApi))
        ? Math.max(0, Math.floor(Number(delayFromApi)))
        : delayFallback != null && Number.isFinite(Number(delayFallback))
          ? Math.max(0, Math.floor(Number(delayFallback)))
          : 0,
    isActive: obl.is_active !== false,
    status: obl.status,
  };
}

const OBLIGATION_STAGE_ORDER: Record<string, number> = { PREPAYMENT: 0, FINAL: 1, INVOICE: 2 };

export function sortedClientIncomeObligations(
  application: Application
): ApplicationClientFinancialObligation[] {
  return clientIncomeObligations(application)
    .slice()
    .sort((a, b) => {
      const sa = OBLIGATION_STAGE_ORDER[String(a.stage || '').toUpperCase()] ?? 9;
      const sb = OBLIGATION_STAGE_ORDER[String(b.stage || '').toUpperCase()] ?? 9;
      return sa - sb || a.id - b.id;
    });
}

function formatPaymentDateLabel(paymentDate: string | null): string {
  const undatedLabel = 'Дата не указана';
  if (!paymentDate) return undatedLabel;
  const d = new Date(paymentDate);
  if (Number.isNaN(d.getTime())) return undatedLabel;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

export function projectObligationClientPayments(
  obl: ApplicationClientFinancialObligation
): ApplicationClientPaymentRow[] {
  const rows: ApplicationClientPaymentRow[] = [];
  for (const p of obl.payments ?? []) {
    const amount = Number(p.amountPaid ?? 0);
    if (amount <= 0) continue;
    const paymentDate = p.paymentDate ? String(p.paymentDate) : null;
    rows.push({
      key: `${obl.id}-${paymentDate ?? 'nodate'}-${amount}`,
      paymentDate,
      dateLabel: formatPaymentDateLabel(paymentDate),
      amount,
    });
  }
  return rows.sort((a, b) => {
    if (a.paymentDate && b.paymentDate) {
      return new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime();
    }
    if (a.paymentDate && !b.paymentDate) return -1;
    if (!a.paymentDate && b.paymentDate) return 1;
    return 0;
  });
}

function obligationRemaining(o: ApplicationClientFinancialObligation, planned: number, paid: number): number {
  const rem = Number((o as { remaining_amount?: number }).remaining_amount ?? NaN);
  if (Number.isFinite(rem)) return Math.max(0, rem);
  return Math.max(0, Math.round((planned - paid) * 100) / 100);
}

/** Клиентские плановые обязательства (как в ApplicationDetailsPanel, без adjustment). */
export function clientPlannedObligations(application: Application): ApplicationClientFinancialObligation[] {
  return (application.financialObligations ?? []).filter(
    (o) =>
      String(o.direction || '').toUpperCase() === 'INCOME' &&
      String(o.counterpartyType || 'CLIENT').toUpperCase() === 'CLIENT' &&
      !(o as { isAdjustment?: boolean }).isAdjustment
  );
}

/** Полная сумма договорённости с клиентом (fallback без obligations). */
export function applicationContractTotalRequired(app: Application): number {
  const raw = app.totalCostWithVAT ?? app.clientRate ?? 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Сумма оплат по всем обязательствам заявки (как в ApplicationDetailsPanel). */
export function sumPaidAllClientObligations(
  rows: ApplicationClientFinancialObligation[] | undefined
): number {
  if (!rows?.length) return 0;
  return Math.round(rows.reduce((s, obl) => s + sumPaidClientObligation(obl), 0) * 100) / 100;
}

export function pickClientObligation(
  rows: ApplicationClientFinancialObligation[] | undefined,
  stage: 'PREPAYMENT' | 'FINAL'
): ApplicationClientFinancialObligation | undefined {
  if (!rows?.length) return undefined;
  return rows.find((o) => String(o.stage).toUpperCase() === stage);
}

/** Единая сводная модель клиентских расчётов (полная карточка + drawer). */
export function resolveApplicationClientContractSummary(application: Application): {
  contractTotalRequired: number;
  totalPaidAll: number;
  balanceRemaining: number;
  status: { label: string; color: string };
} {
  const rows = application.financialObligations;
  const planned = clientPlannedObligations(application);
  const plannedSumFromObl = planned.reduce((s, o) => s + Number(o.amountWithVAT ?? 0), 0);
  const contractTotalRequired =
    plannedSumFromObl > 0.005 ? plannedSumFromObl : applicationContractTotalRequired(application);
  const totalPaidAll = sumPaidAllClientObligations(rows);
  const balanceRemaining = Math.max(0, contractTotalRequired - totalPaidAll);
  const prepObl = pickClientObligation(rows, 'PREPAYMENT');
  const finalObl = pickClientObligation(rows, 'FINAL');
  const status = overallClientPaymentStatus(
    contractTotalRequired,
    totalPaidAll,
    [prepObl, finalObl].some((obl) => String(obl?.status || '').toUpperCase() === 'OVERDUE')
  );
  return { contractTotalRequired, totalPaidAll, balanceRemaining, status };
}

function clientIncomeObligations(application: Application): ApplicationClientFinancialObligation[] {
  return (application.financialObligations ?? []).filter(
    (o) =>
      String(o.direction || '').toUpperCase() === 'INCOME' &&
      String(o.counterpartyType || 'CLIENT').toUpperCase() === 'CLIENT' &&
      o.is_active !== false
  );
}

export function clientPaymentStatus(required: number, paid: number, remaining: number): string {
  const eps = 0.01;
  if (!Number.isFinite(required) || required <= eps) return '—';
  if (remaining <= eps) return 'Оплачено';
  if (paid <= eps) return 'Не оплачено';
  return 'Частично оплачено';
}

/** Сводный статус оплаты — та же логика, что в ApplicationDetailsPanel (финансы заявки). */
export function overallClientPaymentStatus(
  required: number,
  paid: number,
  overdue: boolean
): { label: string; color: string } {
  if (required <= 0) return { label: '—', color: 'default' };
  if (Math.max(0, required - paid) <= 0.01) return { label: 'Оплачено', color: 'green' };
  if (overdue) return { label: 'Просрочено', color: 'red' };
  if (paid > 0.01) return { label: 'Ожидается', color: 'gold' };
  return { label: 'Не оплачено', color: 'default' };
}

export function projectApplicationClientSettlement(application: Application): ApplicationClientSettlement {
  const { contractTotalRequired, totalPaidAll, balanceRemaining, status } =
    resolveApplicationClientContractSummary(application);

  return {
    transportCost: contractTotalRequired > 0.005 ? contractTotalRequired : null,
    received: totalPaidAll,
    remaining: balanceRemaining,
    paymentStatus: status.label,
  };
}

function obligationPaidAt(o: ApplicationClientFinancialObligation, paid: number): string | null {
  if (paid <= 0.01) return null;
  const dated = (o.payments ?? [])
    .map((p) => ({
      date: p.paymentDate ? String(p.paymentDate) : null,
      amount: Number(p.amountPaid ?? 0),
    }))
    .filter((p) => p.date && p.amount > 0);
  if (dated.length === 0) return null;
  dated.sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime());
  return dated[0].date;
}

export function projectApplicationClientObligationRows(application: Application): ApplicationClientObligationRow[] {
  return sortedClientIncomeObligations(application).map((o) => {
      const planned = Number(o.planned_amount ?? o.amountWithVAT ?? 0);
      const paid = Number.isFinite(Number(o.paid_amount)) ? Number(o.paid_amount) : obligationPaid(o);
      const remaining = Number.isFinite(Number(o.remaining_amount))
        ? Number(o.remaining_amount)
        : obligationRemaining(o, planned, paid);
      return {
        id: o.id,
        stage: o.stage,
        stageLabel: obligationStageLabel(o.stage),
        amount: planned,
        remaining: Math.max(0, remaining),
        dueDate: o.dueDate ?? null,
        paidAt: obligationPaidAt(o, paid),
        status: o.status,
      };
    });
}

export function projectApplicationClientPayments(application: Application): ApplicationClientPaymentRow[] {
  const rows: ApplicationClientPaymentRow[] = [];
  for (const o of clientIncomeObligations(application)) {
    rows.push(...projectObligationClientPayments(o));
  }

  return rows.sort((a, b) => {
    if (a.paymentDate && b.paymentDate) {
      return new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime();
    }
    if (a.paymentDate && !b.paymentDate) return -1;
    if (!a.paymentDate && b.paymentDate) return 1;
    return 0;
  });
}

export function resolveApplicationFinanceTaskAmount(
  task: { obligationId?: number | null; obligationStage?: string | null },
  application: Application
): number | null {
  const obligations = clientIncomeObligations(application);
  const byId =
    task.obligationId != null ? obligations.find((o) => o.id === task.obligationId) : undefined;
  const byStage =
    !byId && task.obligationStage
      ? obligations.find(
          (o) => String(o.stage || '').toUpperCase() === String(task.obligationStage || '').toUpperCase()
        )
      : undefined;
  const obl = byId ?? byStage;
  if (!obl) return null;
  const amount = Number(obl.planned_amount ?? obl.amountWithVAT ?? 0);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}
